import React, { useCallback, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import { ErrorBoundary } from 'react-error-boundary';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import styled from 'styled-components';

import sendErrorReport from 'common/utils/sendErrorReport';

type Props = {
  alt: string;
  blurhash: string | null;
  className?: string;
  src: string;
  size?: Size;
};

type Size = 'tiny' | 'medium';

const sizeConfig: Record<Size, { size: number; resolution: number }> = {
  tiny: { size: 16, resolution: 12 },
  medium: { size: 56, resolution: 32 },
};

const Root = styled.div<{ size: Size }>`
  position: relative;
  width: ${({ size }) => `${sizeConfig[size].size}px`};
  height: ${({ size }) => `${sizeConfig[size].size}px`};
  border-radius: ${({ size }) => (size === 'tiny' ? '4px' : '8px')};
  overflow: hidden;
`;

const Thumbnail = styled(LazyLoadImage)`
  object-fit: cover;
`;

const BlurhashWrapper = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: ${({ show }) => (show ? '1' : '0')};
  transition: opacity 200ms;
`;

type BlurhashErrorWrapperProps = {
  children: React.ReactNode;
};

class BlurhashErrorWrapper extends React.Component<BlurhashErrorWrapperProps, { error?: unknown }> {
  constructor(props: BlurhashErrorWrapperProps) {
    super(props);
    this.state = { error: undefined };
  }

  static getDerivedStateFromError(error: any) {
    return { error };
  }

  render() {
    const { error } = this.state;

    return !!error ? null : this.props.children;
  }
}

const LazyLoadedThumbnail = ({ alt, blurhash, className, src, size = 'medium' }: Props) => {
  const [loaded, setLoaded] = useState(() => {
    const image = new Image();
    image.src = src;

    return image.complete;
  });

  const handleImageLoad = useCallback(() => {
    setLoaded(true);
  }, []);

  return (
    <Root className={className} size={size}>
      {blurhash && (
        <BlurhashErrorWrapper>
          <ErrorBoundary fallback={null} onError={error => sendErrorReport(error, 'blurhash')}>
            <BlurhashWrapper show={loaded === false}>
              <Blurhash
                hash={blurhash}
                width={sizeConfig[size].size}
                height={sizeConfig[size].size}
                resolutionX={sizeConfig[size].resolution}
                resolutionY={sizeConfig[size].resolution}
                punch={1}
              />
            </BlurhashWrapper>
          </ErrorBoundary>
        </BlurhashErrorWrapper>
      )}
      <Thumbnail
        className={className}
        width={sizeConfig[size].size}
        height={sizeConfig[size].size}
        alt={alt}
        onLoad={handleImageLoad}
        src={src}
        useIntersectionObserver
      />
    </Root>
  );
};

export default React.memo(LazyLoadedThumbnail);
